// Layer 4 (UI): Advisory › Value's driver tree (polish.P28c, #715). Locked comp
// `.impeccable/mocks/decision/advisory-stages.html`, family A: the 8 is the
// driver-tree ledger with C's value partition in its head (in place of the
// crimson-filled exposure tile); the full-width card under the row reads the
// selected issue in B's expand layout. Every derivation lives in
// `Scene.transform.ts`; doc: docs/logic/advisory/driver-tree.md.

import { useMemo, type KeyboardEvent, type ReactNode } from 'react';
import { usePersona } from '@/app/providers';
import { SurfacePagePanel } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { TrendChart } from '@/versions/v1/modules/_viz';
import type { DriverIssue, PricedDriverTree } from '@/versions/v1/schema/driver-tree';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { DRIVER_TREE_SEED } from '@/versions/v1/shared/bunker-hill-driver-tree-seed';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';
import { StageCard } from '../StageCard';
import { useWholeRowScroll } from '../WholeRowScroll';
import {
  activeIssuesMetaLine,
  activeDriverIssueCount,
  breadcrumb,
  driverLedgerRow,
  exposureHeadline,
  formatExposurePerDay,
  hypothesisChipLabel,
  issueTrendChart,
  openCaseExposurePerDay,
  operatorLedgerNote,
  operatorPrioritySummary,
  prepareIssues,
  priorityLabel,
  projectionForPersona,
  renderedWhat,
  tarpChip,
  topCountedIssue,
  validateDag,
  valuePartition,
  valuePartitionGrid,
  type DriverPersonaProjection,
  type TarpChipTone,
  type ValuePartition,
  type ValuePartitionGrid,
  type ValuePartitionKind,
} from './Scene.transform';
import {
  STAGE_INK_LINK,
  STAGE_LABEL_CAPS,
  STAGE_MONEY,
  STAGE_MONEY_COMPARISON,
  STAGE_ROW_SELECTED,
  STAGE_ROW_TITLE,
  STAGE_ROWS,
} from '../stage-body-ui';

/** The ledger scrolls under the partition and holds the comp's three whole rows;
 *  the Operator reads no partition (it is drawn in US$), so the ledger takes that
 *  room: as many whole rows as 27rem holds. The body always ends on a row
 *  boundary (`wholeRowScrollHeight`); the classes are the first-paint fallback. */
const LEDGER_SCROLL = 'relative max-h-[16rem] overflow-auto';
const LEDGER_SCROLL_OPERATOR = 'relative max-h-[27rem] overflow-auto';
const LEDGER_FIT_ROWS = 3;
const LEDGER_OPERATOR_BUDGET_REM = 27;

const CHIP_BASE = 'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

/** One TARP tone map (comp `tierPill`): Tier 3 breach, Tier 2 warning, Tier 1 outline, Watch neutral. */
const TARP_CHIP_TONE: Record<TarpChipTone, string> = {
  bad: 'bg-[color-mix(in_srgb,var(--state-blocked)_10%,transparent)] text-[var(--state-blocked)]',
  warn: 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]',
  outline: 'border border-[var(--hairline-strong)] bg-[var(--surface-2)] text-ink-secondary',
  neutral: 'bg-[var(--hairline)] text-ink-secondary',
};

export function TarpChip({ issue }: { issue: DriverIssue }) {
  const chip = tarpChip(issue);
  return <span data-tarp-tier={chip.label} className={cn(CHIP_BASE, TARP_CHIP_TONE[chip.tone])}>{chip.label}</span>;
}

/** The Operator's priority chip reads in the state tones, never crimson. */
const PRIORITY_CHIP_TONE: Record<DriverIssue['severity'], string> = {
  critical: TARP_CHIP_TONE.bad,
  high: TARP_CHIP_TONE.warn,
  medium: TARP_CHIP_TONE.neutral,
};

function PathChips({ issue, className }: { issue: DriverIssue; className?: string }) {
  const crumbs = breadcrumb(issue, DRIVER_TREE_SEED.nodes);
  return (
    <span className={cn('flex flex-wrap items-center gap-x-[3px] gap-y-[3px]', className)}>
      {crumbs.map((crumb, index) => (
        <span key={crumb.id} className="inline-flex items-center gap-[3px]">
          {index > 0 ? <span aria-hidden="true" className="text-[10px] text-muted">›</span> : null}
          <span
            className={cn(
              'whitespace-nowrap rounded-md border bg-[var(--surface-2)] px-1.5 py-px text-[10px] leading-4',
              crumb.leaf
                ? 'border-[var(--ink-tertiary)] font-semibold text-ink-primary'
                : 'border-[var(--hairline-strong)] text-ink-secondary',
            )}
          >
            {crumb.label}
          </span>
        </span>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// The shared read: the priced tree at the cursor, for the 8 and the card.
// ---------------------------------------------------------------------------

export type DriverTreeRead = {
  issues: DriverIssue[];
  pricedSeed: PricedDriverTree;
  projection: DriverPersonaProjection;
  cursor: SeriesTime;
};

export function useDriverTree(): DriverTreeRead {
  const { persona } = usePersona();
  const { state } = useScenario();
  const projection = projectionForPersona(persona);
  const issues = useMemo(() => {
    validateDag(DRIVER_TREE_SEED.nodes, DRIVER_TREE_SEED.edges);
    return prepareIssues(DRIVER_TREE_SEED, state);
  }, [state]);
  const pricedSeed = useMemo(() => ({ ...DRIVER_TREE_SEED, issues }), [issues]);
  return { issues, pricedSeed, projection, cursor: state.cursor };
}

// ---------------------------------------------------------------------------
// The partition (comp C `partition`, compact, in A's head)
// ---------------------------------------------------------------------------

const PARTITION_FILL: Record<ValuePartitionKind, string> = {
  'active-loss': 'bg-[color-mix(in_srgb,var(--ink-primary)_9%,var(--surface-2))]',
  risk: 'bg-[repeating-linear-gradient(135deg,color-mix(in_srgb,var(--ink-primary)_9%,var(--surface-2))_0_4px,var(--surface-2)_4px_8px)]',
  'accruing-penalty': 'border-dashed bg-transparent',
  captured: 'border-[color-mix(in_srgb,var(--state-online)_40%,transparent)] bg-[color-mix(in_srgb,var(--state-online)_10%,transparent)]',
};

function PartitionNode({
  label,
  amount,
  kind,
  widthPct,
  issueId,
  selected,
  onSelect,
  gridArea,
}: {
  label: string;
  amount: number;
  kind: ValuePartitionKind;
  widthPct: number | null;
  /** Placed on the partition grid (row, `start / end`); otherwise sized by `widthPct`. */
  gridArea?: { row: number; column: string };
  issueId: string;
  selected: boolean;
  onSelect: (issueId: string) => void;
}) {
  return (
    <button
      type="button"
      data-partition-node={issueId}
      data-partition-kind={kind}
      aria-pressed={selected}
      title={label}
      onClick={() => onSelect(issueId)}
      style={gridArea ? { gridRow: gridArea.row, gridColumn: gridArea.column } : { flex: widthPct === null ? '0 0 auto' : `0 0 calc(${widthPct}% - 3px)` }}
      className={cn(
        'flex min-w-0 flex-col gap-px overflow-hidden rounded-lg border border-[var(--hairline-strong)] px-1.5 py-1 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]',
        PARTITION_FILL[kind],
        selected && 'border-[var(--color-accent-500)] shadow-[0_0_0_2px_var(--accent-soft)]',
      )}
    >
      <span className="truncate text-[11px] leading-4 text-ink-secondary">{label}</span>
      <span className="whitespace-nowrap text-[13px] font-semibold leading-4 tabular-nums text-ink-primary">{formatExposurePerDay(amount)}</span>
    </button>
  );
}

function LegendSwatch({ kind }: { kind: ValuePartitionKind }) {
  return <i aria-hidden="true" className={cn('mr-1.5 inline-block h-2.5 w-3.5 rounded-[3px] border border-[var(--hairline-strong)] align-[-1px]', PARTITION_FILL[kind])} />;
}

function Partition({
  partition,
  selectedId,
  onSelect,
}: {
  partition: ValuePartition;
  selectedId: string;
  onSelect: (issueId: string) => void;
}) {
  const grid = valuePartitionGrid(partition);
  const columns = grid.tracks
    .map((track) => `minmax(${track.fitLabel ? 'max-content' : '0'}, ${track.pct}fr)`)
    .join(' ');
  // One issue can sit on both rows (the P80 excursion's counted arm and its
  // recovery share), so each row keeps its own placements.
  const placeOn = (cells: ValuePartitionGrid['counted'], gridRow: number) =>
    new Map(cells.map((cell) => [cell.issueId, { row: gridRow, column: `${cell.start} / ${cell.end}` }]));
  const countedPlace = placeOn(grid.counted, 1);
  const sharesPlace = placeOn(grid.shares, 2);
  const node = (row: ValuePartition['counted'][number], place?: Map<string, { row: number; column: string }>) => (
    <PartitionNode
      key={row.issueId}
      label={row.label}
      amount={row.amountPerDay}
      kind={row.kind}
      widthPct={row.widthPct}
      gridArea={place?.get(row.issueId)}
      issueId={row.issueId}
      selected={row.issueId === selectedId}
      onSelect={onSelect}
    />
  );
  return (
    <div data-value-partition className="flex flex-col gap-1 border-b border-[var(--hairline)] px-4 pb-2.5 pt-2">
      <div className="grid gap-x-[3px] gap-y-1" style={{ gridTemplateColumns: columns }}>
        <div data-partition-level="counted" className="contents">{partition.counted.map((row) => node(row, countedPlace))}</div>
        {partition.shares.length > 0 ? (
          <div data-partition-level="shares" className="contents">{partition.shares.map((row) => node(row, sharesPlace))}</div>
        ) : null}
      </div>
      {partition.captured.map((row) => (
        <div key={row.issueId} data-partition-level="captured" className="flex items-center gap-2.5">
          {node(row)}
          <span className="min-w-0 text-xs text-muted">{`left the ${row.ownerLabel} owner at ${row.at}; counted under Value, not in exposure`}</span>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 pt-0.5 text-[11px] text-muted">
        <span><LegendSwatch kind="active-loss" />active loss</span>
        <span><LegendSwatch kind="risk" />risk</span>
        <span><LegendSwatch kind="accruing-penalty" />accruing penalty</span>
        <span><LegendSwatch kind="captured" />captured</span>
        <span>{`width = open ${CURRENCY_GLYPH}/day; shares nest inside their owner and count once`}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The ledger (comp A `dtLedger`)
// ---------------------------------------------------------------------------

function LedgerBar({ pct, tone }: { pct: number; tone: 'ink' | 'soft' | 'ok' }) {
  return (
    <span className="relative ml-auto block h-[5px] w-full max-w-[9rem] overflow-hidden rounded-full bg-[var(--hairline)]" aria-hidden="true">
      <span
        data-ledger-bar={tone}
        className={cn(
          'absolute inset-y-0 left-0 rounded-full',
          tone === 'ink' ? 'bg-[var(--ink-secondary)]' : tone === 'ok' ? 'bg-[var(--state-online)]' : 'bg-[color-mix(in_srgb,var(--ink-primary)_18%,transparent)]',
        )}
        style={{ width: `${pct}%` }}
      />
    </span>
  );
}

function LedgerRow({
  issue,
  read,
  top,
  selected,
  onSelect,
  onOpenCase,
}: {
  issue: DriverIssue;
  read: DriverTreeRead;
  /** The highest counted issue: the bars' 100 %. */
  top: DriverIssue;
  selected: boolean;
  onSelect: () => void;
  onOpenCase?: (caseId: string) => void;
}) {
  const row = driverLedgerRow(issue, read.pricedSeed, read.projection, read.cursor, top);
  const topRank = row.rank === 1;
  const operator = read.projection === 'operator';
  const note = operator ? operatorLedgerNote(issue, read.pricedSeed, read.cursor) : row.note;
  const select = (event?: KeyboardEvent<HTMLElement>) => {
    if (event && event.key !== 'Enter' && event.key !== ' ') return;
    event?.preventDefault();
    onSelect();
  };
  return (
    <li
      id={`driver-issue-${issue.id}`}
      className={cn(
        'relative grid grid-cols-[2.25rem_minmax(0,1fr)_minmax(9rem,13rem)_minmax(8.5rem,11rem)] items-center gap-x-2 py-2 pr-4 transition-colors duration-150',
        selected ? STAGE_ROW_SELECTED : 'hover:bg-[var(--surface-1)]',
      )}
    >
      <span
        data-ledger-rank={row.rank}
        className={cn('text-center text-[13px] font-semibold tabular-nums', topRank ? 'text-[var(--color-accent-500)]' : 'text-muted')}
      >
        {row.rank}
      </span>
      <div className="min-w-0">
        {/* The row's own control: the title stretches over the whole row, so a
            click anywhere selects it; the case link sits above it. */}
        <button
          type="button"
          data-driver-issue-id={issue.id}
          data-driver-issue-state={row.resolved ? 'resolved' : 'active'}
          aria-expanded={selected}
          onClick={onSelect}
          onKeyDown={(event) => select(event)}
          className={cn(STAGE_ROW_TITLE, 'text-left after:absolute after:inset-0 after:content-[""] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-[var(--color-accent-500)]')}
        >
          {row.title}
          {row.oneOff ? <span className="sr-only"> · one-off stop comparison</span> : null}
        </button>
        <PathChips issue={issue} className="mt-1" />
      </div>
      <div className="min-w-0">
        <TarpChip issue={issue} />
        <div className="mt-1 text-[11px] leading-snug text-muted">{note}</div>
        {issue.caseId && onOpenCase ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenCase(issue.caseId!);
            }}
            className={cn(STAGE_INK_LINK, 'relative z-10 mt-1')}
          >
            Open case ›
          </button>
        ) : null}
      </div>
      <div className="min-w-0 text-right">
        {operator ? (
          <>
            <span className={cn(CHIP_BASE, PRIORITY_CHIP_TONE[issue.severity])}>{priorityLabel(issue.severity)}</span>
            {row.barPct !== null ? <div className="mt-1.5"><LedgerBar pct={row.barPct} tone={row.barTone} /></div> : null}
          </>
        ) : (
          <>
            <div className={cn(STAGE_MONEY, row.muted && 'text-muted')}>{row.figure}</div>
            {row.comparison.map((line) => (
              <div key={line} className={STAGE_MONEY_COMPARISON}>{line}</div>
            ))}
            {row.barPct !== null ? <div className="mt-1"><LedgerBar pct={row.barPct} tone={row.barTone} /></div> : null}
          </>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// The 8: Driver-tree exposure
// ---------------------------------------------------------------------------

export function DriverTreeScene({
  read,
  selectedId,
  onSelect,
  onOpenCase,
}: {
  read: DriverTreeRead;
  selectedId: string;
  onSelect: (issueId: string) => void;
  onOpenCase?: (caseId: string) => void;
}) {
  const { issues, pricedSeed, projection, cursor } = read;
  const top = topCountedIssue(issues);
  // D20: the head reads the same open-case exposure as the GM tile and the
  // flow landing's at-stake figure — captured value belongs to the Value stage
  // and is not part of this headline.
  const exposure = useMemo(
    () => openCaseExposurePerDay(pricedSeed, cursor),
    [pricedSeed, cursor],
  );
  const headline = exposureHeadline(exposure);
  const activeCount = activeDriverIssueCount(issues, cursor);
  const prioritySummary = useMemo(() => operatorPrioritySummary(issues, cursor), [issues, cursor]);
  const partition = useMemo(() => valuePartition(pricedSeed, cursor), [pricedSeed, cursor]);
  const operator = projection === 'operator';
  const ledgerScroll = useWholeRowScroll<HTMLUListElement>(
    operator
      ? { kind: 'budget', budgetRem: LEDGER_OPERATOR_BUDGET_REM, minRows: LEDGER_FIT_ROWS }
      : { kind: 'rows', rows: LEDGER_FIT_ROWS },
  );

  return (
    <Commentable name="DriverTree">
      <div className="h-full">
        <SurfacePagePanel title="Driver-tree exposure" flush className="h-full" contentClassName="pb-0">
          <div data-driver-tree-head className="flex flex-wrap items-end gap-3.5 border-b border-[var(--hairline)] px-4 py-2.5">
            {operator ? (
              <div>
                <p className={STAGE_LABEL_CAPS}>Priority</p>
                <p className="mt-0.5 flex flex-wrap items-baseline gap-x-3 text-lg font-semibold leading-tight tabular-nums text-ink-primary">
                  {prioritySummary.map((row) => (
                    <span key={row.severity}>{row.count} <span className="text-xs font-semibold tracking-[0.05em] text-ink-secondary">{row.label}</span></span>
                  ))}
                </p>
              </div>
            ) : (
              <div>
                <p className={STAGE_LABEL_CAPS}>Total EBITDA exposure</p>
                <p className="mt-0.5 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
                  {headline.total}
                  <span className="text-xs font-medium tracking-normal text-muted"> /day</span>
                </p>
                <p data-driver-tree-split className="mt-1 text-xs tabular-nums text-muted">{headline.split}</p>
              </div>
            )}
            <span className="ml-auto text-xs text-muted">{activeIssuesMetaLine(projection, activeCount)}</span>
          </div>
          {operator ? null : <Partition partition={partition} selectedId={selectedId} onSelect={onSelect} />}
          <ul ref={ledgerScroll.ref} style={ledgerScroll.style} data-driver-ledger className={cn(operator ? LEDGER_SCROLL_OPERATOR : LEDGER_SCROLL, STAGE_ROWS)}>
            {issues.map((issue) => (
              <LedgerRow
                key={issue.id}
                issue={issue}
                read={read}
                top={top}
                selected={issue.id === selectedId}
                onSelect={() => onSelect(issue.id)}
                onOpenCase={onOpenCase}
              />
            ))}
          </ul>
        </SurfacePagePanel>
      </div>
    </Commentable>
  );
}

// ---------------------------------------------------------------------------
// The full-width card: the selected issue, in B's expand layout
// ---------------------------------------------------------------------------

function NumberedActions({ actions }: { actions: readonly string[] }) {
  return (
    <ol className="space-y-1.5">
      {actions.map((action, index) => (
        <li key={index} className="flex gap-2 text-xs leading-relaxed text-ink-secondary">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-2)] text-[10px] font-semibold tabular-nums text-ink-primary">
            {index + 1}
          </span>
          <span>{action}</span>
        </li>
      ))}
    </ol>
  );
}

function DetailBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className={STAGE_LABEL_CAPS}>{label}</p>
      <div className="mt-1 text-xs leading-relaxed text-ink-secondary">{children}</div>
    </div>
  );
}

export function DriverIssueCard({
  read,
  issueId,
  onOpenCase,
}: {
  read: DriverTreeRead;
  issueId: string;
  onOpenCase?: (caseId: string) => void;
}) {
  const issue = read.issues.find((row) => row.id === issueId) ?? read.issues[0]!;
  const projection = read.projection;
  const chart = issueTrendChart(issue);
  const openCase = issue.caseId && onOpenCase ? (
    <button type="button" onClick={() => onOpenCase(issue.caseId!)} className={STAGE_INK_LINK}>Open case ›</button>
  ) : null;
  return (
    <Commentable name="SelectedIssue">
      <div data-driver-issue-detail={issue.id}>
        <StageCard title={issue.title} beside={<TarpChip issue={issue} />} actions={<PathChips issue={issue} />}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 px-4 py-3 md:grid-cols-[20rem_minmax(0,1fr)]">
            <div className="min-w-0">
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <p className={STAGE_LABEL_CAPS}>{issue.trend.label}</p>
                <p className="text-[10px] tabular-nums text-muted">{chart.lastValueLabel}</p>
              </div>
              <TrendChart
                series={chart.series}
                categories={chart.categories}
                references={chart.references}
                yDomain={chart.yDomain}
                height={130}
              />
            </div>
            <div className="min-w-0 divide-y divide-[var(--hairline)]">
              <div className="pb-2.5">
                <DetailBlock label="What is happening">{renderedWhat(issue, projection)}</DetailBlock>
              </div>
              <div className="pt-2.5">
                <DetailBlock label="Why it is happening">{issue.why}</DetailBlock>
              </div>
            </div>
          </div>
          <div className="border-t border-[var(--hairline)] px-4 py-3">
            {projection === 'metallurgist' ? (
              <DetailBlock label="Diagnosis and financial context">
                <p>{issue.metDiagnosis}</p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {issue.hypotheses.map((hyp, index) => (
                    <span
                      key={index}
                      className={cn(
                        'inline-flex items-center rounded-md border bg-[var(--surface-2)] px-1.5 py-px text-[10px] leading-4 tabular-nums',
                        index === 0 ? 'border-[var(--ink-tertiary)] font-semibold text-ink-primary' : 'border-[var(--hairline-strong)] text-ink-secondary',
                      )}
                    >
                      {hypothesisChipLabel(hyp)}
                    </span>
                  ))}
                  <span className="ml-auto">{openCase}</span>
                </div>
              </DetailBlock>
            ) : (
              <DetailBlock label={projection === 'operator' ? 'Actions — in order' : 'Maintenance actions'}>
                <NumberedActions actions={projection === 'operator' ? issue.operatorActions : issue.maintenanceActions} />
                {openCase ? <div className="mt-2 text-right">{openCase}</div> : null}
              </DetailBlock>
            )}
          </div>
        </StageCard>
      </div>
    </Commentable>
  );
}
